import React, { memo } from 'react';

import { Stack } from '@/components/layout';
import { ExpandableText, Text } from '@/components/ui';
import { createStyles, useScaling } from '@/hooks';
import { guardianColors } from '@/theme/colors';

import type { PlayerCoachNote } from './types';
import { formatNoteDate } from './utils';

// Figma "Kit & Notes" note card fill — not present in the shared palette tokens.
const NOTE_CARD_BACKGROUND = '#FFFBEB';

type CoachNoteCardProps = {
  note: PlayerCoachNote;
};

/** One coach note: coach name + created date header, 3-line body with view more/show less. */
function CoachNoteCard({ note }: CoachNoteCardProps) {
  const styles = useStyles();
  const { scale } = useScaling();

  return (
    <Stack style={styles.card}>
      <Stack direction="row" align="center" gap={scale(4)}>
        <Text
          variant="Font Guardian App/Subtitle/Subtitle 12 Bold"
          numberOfLines={1}
          style={styles.coachName}>
          {note.coachName}
        </Text>
        <Text
          variant="Font Guardian App/Subtitle/Subtitle 11 Regular"
          numberOfLines={1}
          style={styles.date}>
          {formatNoteDate(note.date)}
        </Text>
      </Stack>

      <ExpandableText
        variant="Font Guardian App/Subtitle/Subtitle 14 Regular"
        textStyle={styles.body}
        style={styles.bodyWrap}>
        {note.body}
      </ExpandableText>
    </Stack>
  );
}

const useStyles = createStyles(({ scale }) => ({
  card: {
    paddingHorizontal: scale(16),
    paddingVertical: scale(12),
    borderWidth: 0.5,
    borderColor: guardianColors['12'],
    borderRadius: scale(12),
    backgroundColor: NOTE_CARD_BACKGROUND,
  },
  coachName: {
    flexShrink: 1,
    color: guardianColors['10'],
  },
  date: {
    flex: 1,
    textAlign: 'right',
    color: guardianColors['6'],
  },
  bodyWrap: {
    marginTop: scale(8),
  },
  body: {
    color: guardianColors['10'],
  },
}));

export default memo(CoachNoteCard);
